import { useEffect, useState } from "react";
import { Card, Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Form, FormCheckbox, SubmitButton, useFormContext } from "@jsq/ds/forms";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { useFeatureFlag } from "@jsq/flags-react";
import { withClientContext } from "~/apollo/client";
import {
  useGetEntityDeltasQuery,
  useGetEntityTransferSettingsQuery,
  useGetEntityMixFundStatusLazyQuery,
} from "~/graphql/mainHooks";
import { type GetEntityDeltasQuery } from "~/graphql/mainTypes";
import { SyncStatus } from "~/graphql/supergraphEnums";
import {
  type GetEntitiesSyncStatusQueryResult,
  type GetTransferBatchesQueryResult,
  useGetEntityGlSyncDetailsQuery,
  useGetEntityGlSyncSettingsQuery,
  useGetTransferBatchesQuery,
} from "~/graphql/supergraphHooks";
import { type GlBatch } from "~/graphql/supergraphTypes";
import { useDecoupleCanopyTransferSyncFlag } from "~/pages/FundAccounting/flags";
import { formatDate } from "~/pages/FundAccounting/utils/helpers";
import { SPACING } from "~/styles/variables";
import { DATE_FORMATS } from "~/utils/constants";
import { formatFullDateAndTimeWithLocalTimezone, toStandardFormat } from "~/utils/datetime";
import { getErrorMessage } from "~/utils/errors";
import { toFederationGlobalId, toGlobalId } from "~/utils/graphql-helpers";
import { getCapitalActivityLabel, TransactionCategory } from "../../../types";
import TransferSlideout from "../TransferSlideout";
import { populatePositionTransferDateMap } from "../TransferSlideout/UnsyncedTable/utils";
import CannotSyncBanner from "./CannotSyncBanner";
import { INITIAL_VALUES, SYNC_SETTINGS_TYPES } from "./constants";
import CustomEndDateCard from "./CustomEndDateCard";
import FirstTimeUpdateCard from "./FirstTimeUpdateCard";
import { getEndDate, handleValidate, normalizeSharedGlStartDate } from "./helpers";
import SyncDetailsErrorState from "./States/SyncDetailsErrorState";
import TransferBanner from "./TransferBanner";
import { InitialValues, TransactionType } from "./types";

const INCEPTION_DATE = "Inception";

type UnsyncedGlBatch = Omit<GlBatch, "lastSyncAt">;

type QueryResult = NonNullable<
  GetTransferBatchesQueryResult["data"]
>["entityTransferGlBatchesDetails"];

type EntityGlSyncStatusResponse = NonNullable<
  GetEntitiesSyncStatusQueryResult["data"]
>["entitiesGLSyncStatus"][0];

type Props = {
  onSubmit?: (value: InitialValues) => Promise<void>;
  entityId?: number;
  arenaId: number;
  entityName?: string;
  arenaName: string;
  arenaSubdomain?: string;
  defaultSyncTransactionCategory?: string;
  syncStatuses?: EntityGlSyncStatusResponse;
  onSyncTransfer: () => void;
};

const SyncCheckbox = ({
  name,
  label,
  count = 0,
  lastSync = undefined,
  lastSyncUser = undefined,
  disabled = false,
}: {
  name: string;
  label: string;
  count: number;
  lastSync: string | undefined;
  lastSyncUser: string | null | undefined;
  disabled: boolean | undefined;
}) => (
  <div style={{ marginBottom: SPACING.SMALL }}>
    <FormCheckbox name={name} label={label} disabled={disabled} />
    {!disabled && (
      <div style={{ marginLeft: SPACING.LARGE }}>
        {count == 1 && <Text variant="secondary-sm">{count} unsynced transaction</Text>}
        {count != 1 && <Text variant="secondary-sm">{count} unsynced transactions</Text>}
        <Text variant={"secondary-sm"}>
          {lastSync
            ? "Last synced " +
              formatFullDateAndTimeWithLocalTimezone(lastSync, "", true) +
              " by " +
              (lastSyncUser ? lastSyncUser : " Unknown User")
            : ""}
        </Text>
      </div>
    )}
    {disabled && (
      <div style={{ marginLeft: SPACING.LARGE }}>
        <Text variant="secondary-sm">Sync in progress...</Text>
      </div>
    )}
  </div>
);

const UpdateDateRange = ({
  onSubmit,
  entityId,
  arenaId,
  entityName,
  arenaName,
  arenaSubdomain,
  defaultSyncTransactionCategory,
  syncStatuses,
  onSyncTransfer,
}: Props) => {
  const [checkedCapitalCall, setCheckedCapitalCall] = useState<boolean>(false);
  const [checkedCapitalTransaction, setCheckedCapitalTransaction] = useState<boolean>(false);
  const [checkedDistribution, setCheckedDistribution] = useState<boolean>(false);
  const [checkedNetIncome, setCheckedNetIncome] = useState<boolean>(false);
  const [formattedMaxGlAsOfDate, setFormattedMaxGlAsOfDate] = useState<string>("");
  const [shortFormattedGlStartDate, setShortFormattedGlStartDate] = useState<string>("");
  const [newTransactionCountSum, setNewTransactionCountSum] = useState<number>(0);
  const [revisedTransactionCountSum, setRevisedTransactionCountSum] = useState<number>(0);
  const [showTransferGlBatchSlideout, setShowTransferGlBatchSlideout] = useState(false);
  const [disableSync, setDisableSync] = useState(false);
  const [transferBeforeStartDateError, setTransferBeforeStartDateError] = useState(false);
  const [hasRange, setHasRange] = useState<boolean | null>(null);

  const decoupleCanopyTransferSync = useDecoupleCanopyTransferSyncFlag();
  const isCanopyTransferErrorBannerEnabled = useFeatureFlag("canopyTransferErrorBanner", false);
  const showCapitalActivityLabelCanopy = useFeatureFlag(
    "showCapitalActivityLabelCanopy",
    false,
  );
  const capitalActivityLabel = getCapitalActivityLabel(showCapitalActivityLabelCanopy);

  INITIAL_VALUES.capitalCalls =
    defaultSyncTransactionCategory == TransactionCategory.CapitalCalls;
  INITIAL_VALUES.capitalActivity =
    defaultSyncTransactionCategory == TransactionCategory.Redemptions;
  INITIAL_VALUES.distributions =
    defaultSyncTransactionCategory == TransactionCategory.Distributions;
  INITIAL_VALUES.netIncome = defaultSyncTransactionCategory == TransactionCategory.NetIncome;

  const globalArenaId = toFederationGlobalId("ArenaObjectType", arenaId ? arenaId : "-1");
  const selectedEntityId = entityId ? entityId : -1;

  const [fetchMixFundStatus, { data: mixFundStatusData }] = useGetEntityMixFundStatusLazyQuery({
    context: withClientContext("staff", {
      arenaId: arenaId,
    }),
    fetchPolicy: "network-only",
  });

  const { data: transferSettingsData, loading: transferSettingsLoading } =
    useGetEntityTransferSettingsQuery({
      variables: {
        entityId: toGlobalId("AccountObjectType", entityId || -1),
      },
      context: withClientContext("staff", {
        arenaId: arenaId,
      }),
      fetchPolicy: "no-cache",
      skip: decoupleCanopyTransferSync,
    });
  const entityTransferAccountingEnabled =
    transferSettingsData?.investmentEntity?.transferAccountingEnabled || false;

  const { data: deltaResponse, loading: deltaLoading } = useGetEntityDeltasQuery({
    context: withClientContext("staff", {
      arenaId,
    }),
    fetchPolicy: "no-cache",
    variables: {
      entitiesFilters: {
        ids: [toGlobalId("AccountObjectType", entityId || -1)],
      },
      transferOnly: true,
    },
    skip: decoupleCanopyTransferSync,
  }) as { data: GetEntityDeltasQuery; loading: boolean };

  const deltaData = deltaResponse?.investmentEntities?.edges[0]?.node?.deltas;
  const transferDatePositionMap = deltaData ? populatePositionTransferDateMap(deltaData) : {};

  const { data: transferBatchData } = useGetTransferBatchesQuery({
    variables: {
      entityId: entityId || -1,
      arenaId: arenaId || -1,
      isProcessed: false,
    },

    skip: decoupleCanopyTransferSync || !entityId || !arenaId,
    fetchPolicy: "network-only",
  });
  const glBatchData: QueryResult = transferBatchData
    ? transferBatchData.entityTransferGlBatchesDetails
    : { unsynced: [], history: [] };

  const { data: entityGlSyncSettingsData, loading: entityGlSyncSettingLoading } =
    useGetEntityGlSyncSettingsQuery({
      variables: {
        arenaId: globalArenaId,
        entityId: selectedEntityId,
      },

      fetchPolicy: "network-only",
    });
  const transferSyncSettings = entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings;
  const {
    data: entitySyncDetailsData,
    loading,
    error: entitySyncDetailsError,
    refetch: refetchEntitySyncDetails,
  } = useGetEntityGlSyncDetailsQuery({
    variables: {
      entityId: entityId || -1,
      arenaId: globalArenaId,
    },

    fetchPolicy: "network-only",
    skip: !arenaId || !entityId,
  });

  const capitalCallDetails = entitySyncDetailsData?.entityGLSyncDetails?.capitalCallDetails;
  const capitalTransactionDetails =
    entitySyncDetailsData?.entityGLSyncDetails?.capitalTransactionDetails;
  const distributionDetails = entitySyncDetailsData?.entityGLSyncDetails?.distributionDetails;
  const netIncomeDetails = entitySyncDetailsData?.entityGLSyncDetails?.netIncomeDetails;

  const {
    glAsOfDate: glAsOfDateCapitalCall,
    glStartDate: glStartDateCapitalCall,
    lastSyncAt: lastSyncAtCapitalCall,
    newTransactionCount: newTransactionCountCapitalCall,
    revisedTransactionCount: revisedTransactionCountCapitalCall,
    lastSyncUserName: lastSyncUserNameCapitalCall,
  } = capitalCallDetails || {};

  const {
    glAsOfDate: glAsOfDateCapitalTransaction,
    glStartDate: glStartDateCapitalTransaction,
    lastSyncAt: lastSyncAtCapitalTransaction,
    newTransactionCount: newTransactionCountCapitalTransaction,
    revisedTransactionCount: revisedTransactionCountCapitalTransaction,
    lastSyncUserName: lastSyncUserNameCapitalTransaction,
  } = capitalTransactionDetails || {};

  const {
    glAsOfDate: glAsOfDateDistribution,
    glStartDate: glStartDateDistribution,
    lastSyncAt: lastSyncAtDistribution,
    newTransactionCount: newTransactionCountDistribution,
    revisedTransactionCount: revisedTransactionCountDistribution,
    lastSyncUserName: lastSyncUserNameDistribution,
  } = distributionDetails || {};

  const {
    glAsOfDate: glAsOfDateNetIncome,
    glStartDate: glStartDateNetIncome,
    lastSyncAt: lastSyncAtNetIncome,
    newTransactionCount: newTransactionCountNetIncome,
    revisedTransactionCount: revisedTransactionCountNetIncome,
    lastSyncUserName: lastSyncUserNameNetIncome,
  } = netIncomeDetails || {};

  // normalizeSharedGlStartDate moved to helpers for testability

  const handleSubmit = (values: InitialValues) => {
    const finalStartDate = getStartDate(values);
    values.integrationStart =
      finalStartDate && finalStartDate !== INCEPTION_DATE
        ? new Date(`${finalStartDate}T00:00:00`) // add timezone to prevent it interprate "2011-06-01" as "Tue May 31 2011 20:00:00 GMT-0400 (Eastern Daylight Time)"
        : null;
    void onSubmit?.(values);
  };
  const isLoading =
    loading || entityGlSyncSettingLoading || deltaLoading || transferSettingsLoading;

  // on the first render, validate the form
  useEffect(() => {
    if (!isLoading && !entitySyncDetailsError) {
      onValidate(INITIAL_VALUES);
    }
  }, [isLoading, entitySyncDetailsError]);
  if (isLoading) {
    return <LoadingIndicator />;
  }

  // Without this guard a failed GetEntityGlSyncDetails response is indistinguishable
  // from a never-synced entity and the first-time card renders instead (ENG-30811).
  if (entitySyncDetailsError) {
    return (
      <SyncDetailsErrorState
        entityName={entityName}
        errorMessage={getErrorMessage(entitySyncDetailsError)}
        onRetry={() => void refetchEntitySyncDetails()}
      />
    );
  }

  const shortFormattedGlStartDates = {
    [TransactionType.capitalCalls]: glStartDateCapitalCall
      ? toStandardFormat(glStartDateCapitalCall, DATE_FORMATS["MM/DD/YYYY"].short)
      : INCEPTION_DATE,
    [TransactionType.capitalActivity]: glStartDateCapitalTransaction
      ? toStandardFormat(glStartDateCapitalTransaction, DATE_FORMATS["MM/DD/YYYY"].short)
      : INCEPTION_DATE,
    [TransactionType.distributions]: glStartDateDistribution
      ? toStandardFormat(glStartDateDistribution, DATE_FORMATS["MM/DD/YYYY"].short)
      : INCEPTION_DATE,
    [TransactionType.netIncome]: glStartDateNetIncome
      ? toStandardFormat(glStartDateNetIncome, DATE_FORMATS["MM/DD/YYYY"].short)
      : INCEPTION_DATE,
  };

  const ValidateInputs = () => {
    const { values } = useFormContext<InitialValues>();

    let maxGlAsOfDate = "";
    let minSharedGlStartDate = "";
    let newTotalTransactionCount: number = 0;
    let revisedTotalTransactionCount: number = 0;

    function getMaxDate(glAsOfDateTransactionCategory: string | null | undefined) {
      if (!maxGlAsOfDate) {
        maxGlAsOfDate = glAsOfDateTransactionCategory ?? "";
      } else
        maxGlAsOfDate = glAsOfDateTransactionCategory
          ? glAsOfDateTransactionCategory > maxGlAsOfDate
            ? glAsOfDateTransactionCategory
            : maxGlAsOfDate
          : maxGlAsOfDate;
    }

    // If more than one transaction category is selected and they share a start date, use that, otherwise default to Inception.
    function getMinSharedDate(glStartDateForTransactionCategory: string) {
      if (!minSharedGlStartDate) {
        minSharedGlStartDate = glStartDateForTransactionCategory;
      } else {
        if (minSharedGlStartDate !== INCEPTION_DATE) {
          minSharedGlStartDate = glStartDateForTransactionCategory
            ? glStartDateForTransactionCategory !== minSharedGlStartDate
              ? INCEPTION_DATE
              : minSharedGlStartDate
            : INCEPTION_DATE;
        }
      }
    }

    function addTransactionCounts(newCount: number, revisedCount: number) {
      newTotalTransactionCount += newCount;
      revisedTotalTransactionCount += revisedCount;
    }

    if (values.capitalCalls) {
      getMaxDate(glAsOfDateCapitalCall);
      getMinSharedDate(shortFormattedGlStartDates.capitalCalls);
      addTransactionCounts(
        newTransactionCountCapitalCall ?? 0,
        revisedTransactionCountCapitalCall ?? 0,
      );
    }
    if (values.capitalActivity) {
      getMaxDate(glAsOfDateCapitalTransaction);
      getMinSharedDate(shortFormattedGlStartDates.capitalActivity);
      addTransactionCounts(
        newTransactionCountCapitalTransaction ?? 0,
        revisedTransactionCountCapitalTransaction ?? 0,
      );
    }
    if (values.distributions) {
      getMaxDate(glAsOfDateDistribution);
      getMinSharedDate(shortFormattedGlStartDates.distributions);
      addTransactionCounts(
        newTransactionCountDistribution ?? 0,
        revisedTransactionCountDistribution ?? 0,
      );
    }
    if (values.netIncome) {
      getMaxDate(glAsOfDateNetIncome);
      getMinSharedDate(shortFormattedGlStartDates.netIncome);
      addTransactionCounts(
        newTransactionCountNetIncome ?? 0,
        revisedTransactionCountNetIncome ?? 0,
      );
    }

    if (values.syncSettings == SYNC_SETTINGS_TYPES.sinceLastSync) {
      values.integrationStart = null;
      values.useInceptionDate = false;
      values.integrationThrough = null;
    }

    if (values.syncSettings == SYNC_SETTINGS_TYPES.allTransactions) {
      values.customDate = null;
    }

    if (
      newTotalTransactionCount == 0 &&
      revisedTotalTransactionCount == 0 &&
      (values.capitalCalls ||
        values.capitalActivity ||
        values.distributions ||
        values.netIncome) &&
      values.syncSettings == SYNC_SETTINGS_TYPES.sinceLastSync
    ) {
      values.syncSettings = SYNC_SETTINGS_TYPES.empty;
      values.customDate = null;
    }
    values.syncSettings = maxGlAsOfDate ? values.syncSettings : SYNC_SETTINGS_TYPES.empty;

    maxGlAsOfDate = formatDate(maxGlAsOfDate);

    values.firstIntegrationThroughDate = maxGlAsOfDate
      ? null
      : values.firstIntegrationThroughDate;

    values.customDate = maxGlAsOfDate ? values.customDate : null;
    values.updateDateRange = maxGlAsOfDate ? values.updateDateRange : "";

    values.integrationStart = maxGlAsOfDate ? values.integrationStart : null;
    values.useInceptionDate = maxGlAsOfDate ? values.useInceptionDate : false;
    values.integrationThrough = maxGlAsOfDate ? values.integrationThrough : null;

    if (values.useInceptionDate) {
      values.integrationStart = null;
    }
    const disable = getDisableSync(values);
    if (disable !== disableSync) {
      setDisableSync(disable);
    }
    setNewTransactionCountSum(newTotalTransactionCount);
    setRevisedTransactionCountSum(revisedTotalTransactionCount);
    setFormattedMaxGlAsOfDate(maxGlAsOfDate);
    setShortFormattedGlStartDate(minSharedGlStartDate);

    setCheckedCapitalCall(values.capitalCalls);
    setCheckedCapitalTransaction(values.capitalActivity);
    setCheckedDistribution(values.distributions);
    setCheckedNetIncome(values.netIncome);
    return null;
  };

  const showTransferBanner = glBatchData.unsynced.length > 0;

  const anyTransferHasError = (unsyncedTransfer: UnsyncedGlBatch[]): boolean => {
    if (unsyncedTransfer.length === 0) {
      return false;
    }
    return unsyncedTransfer.some((batch) => {
      const batchDate = batch.batchDate || "";
      const positionIds = batch.positionIds || [];
      return !(
        batchDate in transferDatePositionMap &&
        positionIds.every((positionId: number) =>
          transferDatePositionMap[batchDate].has(positionId),
        )
      );
    });
  };

  const getStartDate = (values: InitialValues) => {
    if (
      values.syncSettings === SYNC_SETTINGS_TYPES.allTransactions &&
      values.useInceptionDate
    ) {
      return normalizeSharedGlStartDate(
        values,
        glStartDateCapitalCall ?? null,
        glStartDateCapitalTransaction ?? null,
        glStartDateDistribution ?? null,
        glStartDateNetIncome ?? null,
      );
    }
    const startDate = values.integrationStart
      ? toStandardFormat(values.integrationStart, DATE_FORMATS["MM/DD/YYYY"].short)
      : null;
    const trasnferSyncSettingStartDate =
      !decoupleCanopyTransferSync &&
      transferSyncSettings &&
      transferSyncSettings.active &&
      transferSyncSettings.glStartDate
        ? toStandardFormat(transferSyncSettings.glStartDate, DATE_FORMATS["MM/DD/YYYY"].short)
        : null;

    // Compare with values.integrationStart
    let finalStartDate = null;
    if (trasnferSyncSettingStartDate && startDate) {
      finalStartDate =
        new Date(trasnferSyncSettingStartDate) > new Date(startDate)
          ? trasnferSyncSettingStartDate
          : startDate;
    } else if (trasnferSyncSettingStartDate && !startDate) {
      finalStartDate = trasnferSyncSettingStartDate;
    } else if (!trasnferSyncSettingStartDate && startDate) {
      finalStartDate = startDate;
    }

    return finalStartDate
      ? toStandardFormat(new Date(finalStartDate), DATE_FORMATS["YYYY-MM-DD"].long)
      : null;
  };

  const checkHasMixedFunds = () => {
    if (decoupleCanopyTransferSync || !transferSyncSettings?.active) {
      return false;
    }

    void fetchMixFundStatus({
      variables: {
        entityId: toGlobalId("AccountObjectType", entityId || -1),
      },
    });
  };
  const getDisableSync = (values: InitialValues) => {
    const endDate = getEndDate(values);
    const finalStartDate = getStartDate(values);
    if (
      values.capitalCalls ||
      values.capitalActivity ||
      values.distributions ||
      values.netIncome
    ) {
      if (values.syncSettings !== SYNC_SETTINGS_TYPES.allTransactions) {
        if (endDate) {
          setHasRange(true);
        } else {
          setHasRange(false);
        }
      } else if (finalStartDate && endDate) {
        if (finalStartDate && endDate) {
          setHasRange(true);
        } else {
          setHasRange(false);
        }
      } else {
        setHasRange(false);
      }
    } else {
      setHasRange(false);
    }

    if (endDate == null) {
      return true;
    }

    if (decoupleCanopyTransferSync) {
      return false;
    }

    // transfer sync settings enabled
    const transferSyncSettingsEnabled =
      !!entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings?.active;
    const transferSyncNotEnabled = !transferSyncSettingsEnabled;

    const unsyncedTransfer = glBatchData.unsynced.filter(
      (batch) =>
        endDate == null ||
        endDate == "" ||
        (batch.batchDate != null && batch.batchDate <= endDate),
    );

    const noTransferBatch = glBatchData && unsyncedTransfer.length === 0;
    if (transferSyncNotEnabled || noTransferBatch) {
      return false;
    }

    // check if any transfer is before the start date
    unsyncedTransfer.sort((a: UnsyncedGlBatch, b: UnsyncedGlBatch) => {
      const dateA = a.batchDate ? new Date(a.batchDate).getTime() : 0;
      const dateB = b.batchDate ? new Date(b.batchDate).getTime() : 0;
      return dateA - dateB;
    });

    if (finalStartDate) {
      const beforeStartDateTransfer: UnsyncedGlBatch[] = unsyncedTransfer.filter(
        (batch) =>
          batch.batchDate != null &&
          new Date(batch.batchDate).getTime() < new Date(finalStartDate).getTime(),
      );

      const hasError = anyTransferHasError(beforeStartDateTransfer);
      if (hasError) {
        setTransferBeforeStartDateError(true);
        return true;
      }
    }
    setTransferBeforeStartDateError(false);

    return anyTransferHasError(unsyncedTransfer);
  };

  const onValidate = (values: InitialValues) => {
    checkHasMixedFunds();
    return handleValidate(values);
  };

  const hasMixedFunds =
    !decoupleCanopyTransferSync &&
    transferSyncSettings &&
    transferSyncSettings.active &&
    mixFundStatusData?.investmentEntity?.hasMixedCallsAndStandaloneContributions;

  const SyncErrorBanner =
    deltaResponse != null &&
    deltaLoading === false &&
    hasRange === true &&
    !decoupleCanopyTransferSync &&
    transferSyncSettings &&
    transferSyncSettings.active &&
    (isCanopyTransferErrorBannerEnabled ? showTransferBanner : true) &&
    (disableSync || entityTransferAccountingEnabled || hasMixedFunds) ? (
      <CannotSyncBanner
        arenaName={arenaName}
        entityName={entityName || ""}
        arenaSubdomain={arenaSubdomain || ""}
        entityId={entityId || -1}
        entityTransferEnabled={entityTransferAccountingEnabled}
        transferBeforeStartDateError={transferBeforeStartDateError}
        hasMixedFunds={!!hasMixedFunds}
      />
    ) : null;

  const inProgressSyncStatuses: string[] = [
    SyncStatus.Started,
    SyncStatus.Received,
    SyncStatus.Published,
  ];

  return (
    <>
      {entityId && showTransferGlBatchSlideout && (
        <TransferSlideout
          isShowing={showTransferGlBatchSlideout}
          entityName={entityName || ""}
          onClose={() => {
            setShowTransferGlBatchSlideout(false);
          }}
          onSync={() => {
            onSyncTransfer();
          }}
          selectedEntityId={entityId}
          arenaId={arenaId}
        />
      )}
      <Flex flexDirection="row" style={{ marginBottom: SPACING.XXX_LARGE }}>
        <ColumnContainer>
          <Column size="md">
            <Flex flexDirection="column">
              {glBatchData.unsynced.length > 0 && (
                <TransferBanner
                  onViewDetails={() => {
                    setShowTransferGlBatchSlideout(true);
                  }}
                  onSyncTransfer={() => {
                    setShowTransferGlBatchSlideout(true);
                  }}
                  transferCount={glBatchData.unsynced.length}
                />
              )}
              {SyncErrorBanner}
              <Form
                initialValues={INITIAL_VALUES}
                onSubmit={handleSubmit}
                validate={onValidate}
                submitOnlyChangedValues={false}
              >
                <>
                  <ValidateInputs />
                  <Card
                    style={{ marginTop: showTransferBanner ? SPACING.LARGE : 0 }}
                    className="inset-lg"
                    title="Transaction categories"
                  >
                    <Column size="md">
                      <Flex flexDirection="column">
                        <Text style={{ marginBottom: SPACING.SMALL }}>
                          Select transaction categories to sync
                        </Text>
                        {entityGlSyncSettingsData?.entityGLSyncSettings.capitalCallSettings
                          ?.active && (
                          <SyncCheckbox
                            name="capitalCalls"
                            label={"Capital calls"}
                            count={
                              (newTransactionCountCapitalCall || 0) +
                              (revisedTransactionCountCapitalCall || 0)
                            }
                            lastSync={lastSyncAtCapitalCall ?? undefined}
                            lastSyncUser={lastSyncUserNameCapitalCall}
                            disabled={
                              !!syncStatuses?.capitalCallStatus &&
                              inProgressSyncStatuses.includes(syncStatuses.capitalCallStatus)
                            }
                          />
                        )}
                        {entityGlSyncSettingsData?.entityGLSyncSettings
                          .capitalTransactionSettings?.active && (
                          <SyncCheckbox
                            name="capitalActivity"
                            label={capitalActivityLabel}
                            count={
                              (newTransactionCountCapitalTransaction || 0) +
                              (revisedTransactionCountCapitalTransaction || 0)
                            }
                            lastSync={lastSyncAtCapitalTransaction ?? undefined}
                            lastSyncUser={lastSyncUserNameCapitalTransaction}
                            disabled={
                              !!syncStatuses?.capitalTransactionStatus &&
                              inProgressSyncStatuses.includes(
                                syncStatuses.capitalTransactionStatus,
                              )
                            }
                          />
                        )}
                        {entityGlSyncSettingsData?.entityGLSyncSettings.distributionSettings
                          ?.active && (
                          <SyncCheckbox
                            name="distributions"
                            label={"Distributions"}
                            count={
                              (newTransactionCountDistribution || 0) +
                              (revisedTransactionCountDistribution || 0)
                            }
                            lastSync={lastSyncAtDistribution ?? undefined}
                            lastSyncUser={lastSyncUserNameDistribution}
                            disabled={
                              !!syncStatuses?.distributionStatus &&
                              inProgressSyncStatuses.includes(syncStatuses.distributionStatus)
                            }
                          />
                        )}
                        {entityGlSyncSettingsData?.entityGLSyncSettings.netIncomeSettings
                          ?.active && (
                          <SyncCheckbox
                            name="netIncome"
                            label={"Net income"}
                            count={
                              (newTransactionCountNetIncome || 0) +
                              (revisedTransactionCountNetIncome || 0)
                            }
                            lastSync={lastSyncAtNetIncome ?? undefined}
                            lastSyncUser={lastSyncUserNameNetIncome}
                            disabled={
                              !!syncStatuses?.netIncomeStatus &&
                              inProgressSyncStatuses.includes(syncStatuses.netIncomeStatus)
                            }
                          />
                        )}
                      </Flex>
                    </Column>
                  </Card>
                </>
                {(checkedCapitalCall ||
                  checkedCapitalTransaction ||
                  checkedDistribution ||
                  checkedNetIncome) && (
                  <Card
                    className="inset-lg"
                    title="Sync settings"
                    style={{ marginTop: SPACING.LARGE }}
                  >
                    {!formattedMaxGlAsOfDate && <FirstTimeUpdateCard />}
                    {formattedMaxGlAsOfDate && (
                      <CustomEndDateCard
                        glAsOfDate={formattedMaxGlAsOfDate}
                        glStartDate={shortFormattedGlStartDate}
                        revisedTransactionCount={revisedTransactionCountSum}
                        newTransactionCount={newTransactionCountSum}
                      />
                    )}
                  </Card>
                )}
                <Flex style={{ marginTop: SPACING.LARGE }} justifyContent="flex-end">
                  <SubmitButton
                    disabled={
                      disableSync ||
                      hasMixedFunds ||
                      !hasRange ||
                      (!checkedCapitalCall &&
                        !checkedCapitalTransaction &&
                        !checkedDistribution &&
                        !checkedNetIncome)
                    }
                  >
                    Next
                  </SubmitButton>
                </Flex>
              </Form>
            </Flex>
          </Column>
        </ColumnContainer>
      </Flex>
    </>
  );
};

export default UpdateDateRange;
